import { useEffect, useRef } from 'react';
import { gsap, ScrollTrigger } from '../lib/gsapContext';

const lines: { lead: string; emphasis: string }[] = [
  {
    lead: 'Most consultancies focus on:',
    emphasis: 'building prototypes that survive a demo.',
  },
  {
    lead: 'We focus on:',
    emphasis: 'engineering production systems that survive your firm.',
  },
  {
    lead: 'Most consultancies measure:',
    emphasis: 'hours billed.',
  },
  {
    lead: 'We measure:',
    emphasis: 'documents processed, decisions made, hours returned.',
  },
];

export default function Philosophy() {
  const root = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!root.current) return;
    const ctx = gsap.context(() => {
      const blocks = gsap.utils.toArray<HTMLElement>('[data-philosophy-line]');

      blocks.forEach((el, i) => {
        const isContrast = i % 2 === 1;
        const lead = el.querySelector('[data-lead]');
        const rule = el.querySelector('[data-rule]');
        const emphasis = el.querySelector('[data-emphasis]');
        const words = el.querySelectorAll('[data-emphasis] [data-word]');
        const step = el.querySelector('[data-step]');

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: el,
            start: 'top 78%',
            toggleActions: 'play none none reverse',
          },
        });

        tl.from(step, {
          opacity: 0,
          y: 12,
          duration: 0.5,
          ease: 'power3.out',
        })
          .from(
            lead,
            {
              opacity: 0,
              x: -16,
              duration: 0.5,
              ease: 'power3.out',
            },
            '<0.05'
          )
          .from(
            rule,
            {
              scaleX: 0,
              transformOrigin: 'left center',
              duration: 0.6,
              ease: 'power3.out',
            },
            '<0.1'
          )
          .from(
            words,
            {
              opacity: 0,
              yPercent: 100,
              rotateX: -45,
              transformOrigin: '50% 100% -10px',
              duration: 0.7,
              stagger: 0.04,
              ease: 'power4.out',
            },
            '<0.05'
          );

        if (isContrast) {
          gsap.fromTo(
            emphasis,
            { letterSpacing: '-0.04em' },
            {
              letterSpacing: '0em',
              duration: 1.2,
              ease: 'power3.out',
              scrollTrigger: {
                trigger: el,
                start: 'top 78%',
                toggleActions: 'play none none reverse',
              },
            }
          );
        }
      });
    }, root);

    return () => {
      ctx.revert();
      ScrollTrigger.refresh();
    };
  }, []);

  const split = (s: string) =>
    s.split(' ').map((w, i) => (
      <span key={i} className="mr-[0.25em] inline-block overflow-hidden align-bottom">
        <span data-word className="inline-block">
          {w}
        </span>
      </span>
    ));

  return (
    <section ref={root} className="grain relative bg-black py-32">
      <div className="container-x">
        <p className="label mb-20 inline-flex items-center gap-3">
          <span className="h-px w-10 bg-gold" />
          Philosophy
        </p>

        <div className="grid gap-20 md:gap-32">
          {lines.map((l, i) => {
            const isContrast = i % 2 === 1;
            return (
              <div
                key={i}
                data-philosophy-line
                className="grid max-w-5xl gap-6 md:grid-cols-[80px_1fr]"
              >
                <div
                  data-step
                  className="font-mono text-[0.75rem] uppercase tracking-label text-gold"
                >
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div>
                  <div className="flex items-center gap-4">
                    <p
                      data-lead
                      className="font-mono text-[0.7rem] uppercase tracking-label text-stone"
                    >
                      {l.lead}
                    </p>
                    <span
                      data-rule
                      className="h-px flex-1 bg-gradient-to-r from-gold/60 via-gold/20 to-transparent"
                    />
                  </div>
                  <p
                    data-emphasis
                    className={`mt-6 font-display font-light leading-[1.05] text-balance md:text-6xl text-4xl ${
                      isContrast
                        ? 'italic text-white'
                        : 'text-white/80'
                    }`}
                  >
                    {split(l.emphasis)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
